import type { NodeId } from '@ipfabric/utils';
import { nullable } from '@ipfabric/utils/fp/nullable';
import { plainSet } from '@ipfabric/utils/fp/plainSet';
import { maybePropOfN } from '@ipfabric/utils/fp/prop';
import type { GraphState } from 'components/Graph/GraphState';
import { getEdgeOtherNodeId } from 'components/Graph/helpers/getEdgeOtherNodeId';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import { flow, identity, not } from 'fp-ts/lib/function';

export interface SelectNeighborsCommand {
  nodeId: NodeId;
  distance: number;
}

const addNeighborsOfNode =
  (state: GraphState, toSet: Set<NodeId>) =>
  (nodeId: NodeId): void => {
    const neighborEdgeIds = state.graph.nodesEdgesMap[nodeId];
    if (!neighborEdgeIds) {
      return;
    }
    neighborEdgeIds?.forEach(
      flow(
        maybePropOfN(state.graph.edges),
        nullable.chain(
          flow(
            getEdgeOtherNodeId(nodeId),
            state.peekingAtHiddenNodes
              ? identity
              : nullable.fromPredicate(
                  not(plainSet.isInSet(state.graph.hiddenNodes)),
                ),
          ),
        ),
        nullable.chain(plainSet.insertToMutable(toSet)),
      ),
    );
  };

const addNeighborsOfNodes = (
  state: GraphState,
  discover: Set<NodeId>,
  toResult: Set<NodeId>,
): void => {
  discover.forEach(addNeighborsOfNode(state, toResult));
};

export const selectNeighbors: GraphCommandFactory<SelectNeighborsCommand> =
  (cmdContext) =>
  ({ nodeId, distance }) => {
    cmdContext.updateState((draft) => {
      const result = new Set([nodeId]);
      let discovered = new Set<NodeId>();
      for (let i = 0; i < distance; i += 1) {
        // discover neighbors only of new items
        const toDiscover = plainSet.diff(discovered)(result);

        // push all current items to discovered
        discovered = plainSet.unionAll(discovered, result);

        addNeighborsOfNodes(draft, toDiscover, result);
      }
      const { graph } = draft;
      graph.selectedNodeIds = plainSet.unionAll(graph.selectedNodeIds, result);
    });
  };
